import { EmojiEventsOutlined } from '@mui/icons-material';
import { Button, Chip, ChipProps } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { useConfirmationDialog } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { MessageForm } from '../../components/MessageForm/MessageForm';
import { MessageFormData } from '../../components/MessageForm/MessageForm.schema';
import { DemandAwardingSettings } from '../../DemandAwardingWidget.types';
import { TsrctNegotiationCaseStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useDataGridContext } from '~/components/DataGridCustom';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';

export type ActionCellValue =
  | {
      disabled: boolean;
      caseId: string;
      numCases: number;
      status: TsrctNegotiationCaseStatus;
      awardingSettings: DemandAwardingSettings;
    }
  | null
  | undefined;

type ActionCellOnAwardData = {
  negotiationCaseId: string;
  dismissOtherSuppliers?: boolean;
  informDismissedSuppliers?: boolean;
  informAwardedSupplier?: boolean;
  awardedSupplierMessage?: string;
};
export interface ActionCellContext {
  onAwardClick: (data: ActionCellOnAwardData) => Promise<void>;
}

interface ActionCellProps<R extends GridValidRowModel = any> {
  value: ActionCellValue;
  row: R;
}

const postAwardingStatusColor: Partial<Record<TsrctNegotiationCaseStatus, ChipProps['color']>> = {
  AWARDED: 'success',
  OFFER_DISMISSED: 'default',
};

export const ActionCell = <R extends GridValidRowModel = any>({ value }: ActionCellProps<R>) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandAwardingWidget' });
  const { onAwardClick } = useDataGridContext<ActionCellContext>();
  const { confirm } = useConfirmationDialog();
  const track = useTrackInteraction();
  if (!onAwardClick) {
    throw new Error('ActionCell: DataGridContextProvider must provide a onAwardClick method');
  }

  if (!value) {
    return null;
  }

  const { caseId, disabled, numCases, status, awardingSettings } = value;
  return (
    <>
      {postAwardingStatusColor[status] ? (
        <Chip
          label={t(`cell.status.${status}`)}
          color={postAwardingStatusColor[status]}
          size="small"
          data-intercom-target="Negotiation/StatusChip"
          data-testid="Negotiation/StatusChip"
        />
      ) : (
        <Button
          fullWidth
          size="small"
          disabled={disabled}
          data-testid="Awarding/AwardButton"
          onClick={confirm(
            async (data: MessageFormData) => {
              onAwardClick({
                ...data,
                negotiationCaseId: caseId,
              });
              track(Target.DEMAND, Action.AWARDED, { ...data, negotiationCaseId: caseId });
            },
            {
              icon: <EmojiEventsOutlined />,
              title: t('awardDialog.title'),
              maxWidth: 'xs',
              content: (
                <MessageForm
                  awardingSettings={awardingSettings}
                  numCases={numCases}
                  i18nKeyPrefix={'DemandAwardingWidget.awardDialog'}
                />
              ),
              confirm: {
                children: t('awardDialog.award'),
                color: 'primary',
              },
              cancel: {
                children: t('awardDialog.cancel'),
              },
            },
          )}
        >
          {disabled ? t(`cell.status.${status}`) : t('awardButton')}
        </Button>
      )}
    </>
  );
};
